/**
 * TypeScript types for the Reunification module.
 * Based on ADR-064: ILUG Reunification Complete Specification.
 */
import type { components } from '@tiaki/http-client/types';

// =============================================================================
// Enums / Constants
// =============================================================================

export type ReunificationStatus =
  | 'pending'
  | 'active'
  | 'paused'
  | 'completed'
  | 'cancelled';

export type IncidentType =
  | 'fire'
  | 'lockdown'
  | 'active_threat'
  | 'weather_shelter'
  | 'weather_evac'
  | 'hazmat'
  | 'medical'
  | 'bomb_threat'
  | 'gas_leak'
  | 'other';

export type ReunificationMode = 'onsite' | 'offsite' | 'either';

export type PlanScale =
  | 'single_location'
  | 'multi_location'
  | 'district_aggregate';

export type SiteType =
  | 'live'
  | 'test'
  | 'school'
  | 'mou_location'
  | 'community_center'
  | 'church'
  | 'stadium'
  | 'park'
  | 'parking_lot'
  | 'convention_center'
  | 'ad_hoc'
  | 'other';

export type StaffRole =
  | 'incident_commander'
  | 'greeter'
  | 'checker'
  | 'reunifier'
  | 'class_leader'
  | 'accountant'
  | 'leo_liaison'
  | 'crisis_counselor'
  | 'bus_coordinator'
  | 'runner'
  | 'releaser'
  | 'recorder'
  | 'scribe'
  | 'medical'
  | 'counselor'
  | 'coordinator'
  | 'other';

export type StaffStatus = 'assigned' | 'checked_in' | 'active' | 'released';

export type StudentStatus =
  | 'secured_at_incident'
  | 'transported'
  | 'arrived_at_site'
  | 'in_assembly'
  | 'in_leo_interview'
  | 'in_crisis_counseling'
  | 'cleared_for_release'
  | 'in_reunification_room'
  | 'reunified'
  | 'exited'
  | 'reported_safe_offsite'
  | 'reunified_offsite'
  | 'safe_at_home'
  | 'at_hospital'
  | 'released_to_third_party'
  | 'self_transported'
  | 'absent'
  | 'missing'
  | 'medical'
  | 'unaccounted'
  | 'accounted'
  | 'with_staff'
  | 'released';

export type GuardianStatus =
  | 'notified'
  | 'pre_verified'
  | 'en_route'
  | 'arrived'
  | 'checked_in'
  | 'in_waiting_area'
  | 'matched_to_student'
  | 'reunified'
  | 'exited'
  | 'denied'
  | 'not_arrived'
  | 'verified'
  | 'released';

// =============================================================================
// Metadata & Data Blobs
// =============================================================================

/** Notification templates for a plan (ADR-064) */
export interface PlanNotificationTemplates {
  sms?: string;
  push?: string;
  email?: string;
}

/** Configuration data for a plan or event site */
export type MetadataBlob = Record<string, unknown>;

/** Field definitions for a label template */
export type LabelTemplateFields = Record<string, string | number | boolean | undefined>;

/** Runtime notification settings for an event */
export interface EventNotificationSettings {
  channels: string[];
  notify_on_activation: boolean;
}

/** Emergency contact information for a school */
export interface EmergencyContact {
  contact_name: string;
  relationship: string;
  phone: string;
  email?: string;
}

// =============================================================================
// Domain 1: Plan Templates
// =============================================================================

export interface PlanTemplate {
  public_id: string;
  name: string;
  description: string;
  district: number | null;
  district_name: string;
  incident_type: IncidentType;
  incident_type_display: string;
  reunification_mode: ReunificationMode;
  mode_display: string;
  scale: PlanScale;
  scale_display: string;
  is_system_template: boolean;
  is_published: boolean;
  version: number;
  default_leo_interview: boolean;
  default_counseling: boolean;
  special_procedures: string;
  estimated_duration_hours: number | null;
  floorplan_count: number;
  site_count: number;
  school_count: number;
  created_at: string;
  updated_at: string;
}

export interface PlanTemplateDetail extends PlanTemplate {
  guardian_verification_required: boolean;
  require_id_verification: boolean;
  require_photo_capture: boolean;
  require_signature: boolean;
  notify_on_activation: boolean;
  notification_channels: string[];
  notification_templates: PlanNotificationTemplates;
  floorplans: PlanFloorplan[];
  applicable_sites: PlanApplicableSiteWithPlan[];
  schools: PlanSchool[];
  role_requirements: PlanRoleRequirement[];
  station_requirements: PlanStationRequirement[];
  label_templates: PlanLabelTemplate[];
  created_by: number | null;
  created_by_name: string | null;
  data: MetadataBlob;
}

export interface PlanTemplateWrite {
  name: string;
  description?: string;
  district?: number | null;
  incident_type?: IncidentType;
  reunification_mode?: ReunificationMode;
  scale?: PlanScale;
  default_leo_interview?: boolean;
  default_counseling?: boolean;
  special_procedures?: string;
  estimated_duration_hours?: number | null;
  is_published?: boolean;
  guardian_verification_required?: boolean;
  require_id_verification?: boolean;
  require_photo_capture?: boolean;
  require_signature?: boolean;
  notify_on_activation?: boolean;
  notification_channels?: string[];
  data?: MetadataBlob;
}

export interface PlanFloorplan {
  public_id: string;
  name: string;
  description: string;
  diagram_url: string;
  canvas_width: number;
  canvas_height: number;
  is_primary: boolean;
  sort_order: number;
  stations: PlanStation[];
  zones: PlanZone[];
  flows: PlanFlow[];
}

// Shapes below mirror PlanStationSerializer / PlanZoneSerializer /
// PlanFlowSerializer / PlanFloorplanSerializer — the canvas-based backend
// models.
export interface PlanStation {
  public_id: string;
  name: string;
  description: string;
  station_type: string;
  station_type_display: string;
  canvas_x: number;
  canvas_y: number;
  min_staff: number;
  recommended_staff: number;
  staff_ratio_per_students: number | null;
  capacity: number;
  requires_equipment: string[];
  is_required: boolean;
  sort_order: number;
}

export interface PlanStationWrite {
  name: string;
  description?: string;
  station_type: string;
  canvas_x?: number;
  canvas_y?: number;
  min_staff?: number;
  recommended_staff?: number;
  staff_ratio_per_students?: number | null;
  capacity?: number;
  requires_equipment?: string[];
  is_required?: boolean;
  sort_order?: number;
}

export interface PlanZone {
  public_id: string;
  name: string;
  description: string;
  zone_type: string;
  zone_type_display: string;
  canvas_x: number;
  canvas_y: number;
  canvas_width: number;
  canvas_height: number;
  capacity: number;
  color: string;
}

export interface PlanZoneWrite {
  name: string;
  description?: string;
  zone_type: string;
  canvas_x?: number;
  canvas_y?: number;
  canvas_width?: number;
  canvas_height?: number;
  capacity?: number;
  color?: string;
}

export interface PlanFlow {
  public_id: string;
  flow_type: string;
  flow_type_display: string;
  from_station: string | null;
  from_station_name: string | null;
  to_station: string | null;
  to_station_name: string | null;
  from_zone: string | null;
  from_zone_name: string | null;
  to_zone: string | null;
  to_zone_name: string | null;
  description: string;
  is_primary_path: boolean;
  sequence_order: number;
}

export interface PlanFlowWrite {
  flow_type: string;
  from_station?: string | null;
  to_station?: string | null;
  from_zone?: string | null;
  to_zone?: string | null;
  description?: string;
  is_primary_path?: boolean;
  sequence_order?: number;
}

/** Shape of the floorplan list endpoint (PlanFloorplanListSerializer). */
export interface PlanFloorplanSummary {
  public_id: string;
  name: string;
  description: string;
  sort_order: number;
  station_count: number;
  zone_count: number;
}

export interface PlanFloorplanWrite {
  name: string;
  description?: string;
  diagram_url?: string;
  canvas_width?: number;
  canvas_height?: number;
  is_primary?: boolean;
  sort_order?: number;
}

export interface PlanApplicableSite {
  public_id: string;
  site: string;
  site_name: string;
  site_type: string;
  role: string;
  role_display: string;
  priority_order: number;
  capacity_limit: number | null;
  activation_instructions: string;
  data: MetadataBlob;
  created_at: string;
  updated_at: string;
}

export interface PlanApplicableSiteData {
  staff_assignments?: ConcreteStaffAssignment[];
  station_mappings?: StationMapping[];
  zone_mappings?: ZoneMapping[];
}

export interface PlanApplicableSiteWrite {
  site: string;
  priority_order?: number;
  capacity_sufficient?: boolean;
  notes?: string;
  data?: PlanApplicableSiteData;
}

export interface ConcreteStaffAssignment {
  user_id: number;
  role: StaffRole;
  station_public_id: string | null;
  confirmed: boolean;
}

export interface StationMapping {
  plan_station_public_id: string;
  room_public_id: string;
  notes: string;
  station_type?: string;
  auto_generated?: boolean;
}

export interface ZoneMapping {
  plan_zone_public_id: string;
  room_public_id: string;
  notes: string;
}

export interface PlanApplicableSiteWithPlan extends PlanApplicableSite {
  data: PlanApplicableSiteData & MetadataBlob;
  is_ready: boolean;
  readiness_score: number;
  last_reviewed_at: string | null;
  last_reviewed_by: number | null;
}

/** Helper to extract typed arrays from the data JSON blob. */
export function getSiteData(site: PlanApplicableSiteWithPlan) {
  return {
    staff_assignments: (site.data?.staff_assignments ?? []) as ConcreteStaffAssignment[],
    station_mappings: (site.data?.station_mappings ?? []) as StationMapping[],
    zone_mappings: (site.data?.zone_mappings ?? []) as ZoneMapping[],
  };
}

export interface ReadinessCheckItem {
  name: string;
  status: 'pass' | 'warn' | 'fail';
  detail: string;
}

export interface ReadinessBreakdownItem {
  category: string;
  items: ReadinessCheckItem[];
}

export interface ReadinessBreakdown {
  roles_pct: number;
  stations_pct: number;
  zones_pct: number;
  overall_score: number;
  unfilled_roles: string[];
  unmapped_stations: string[];
  unmapped_zones: string[];
  breakdown?: ReadinessBreakdownItem[];
}

export interface PlanSchool {
  public_id: string;
  school: number;
  school_name: string;
  custom_procedures: string;
  data: MetadataBlob;
  created_at: string;
  updated_at: string;
}

export interface PlanRoleRequirement {
  public_id: string;
  role: StaffRole;
  role_display: string;
  min_count: number;
  recommended_count: number;
  ratio_per_students: number | null;
  requires_training: boolean;
  required_training_ids: string[];
}

export interface PlanRoleRequirementWrite {
  role: StaffRole;
  min_count: number;
  recommended_count: number;
}

export interface PlanStationRequirement {
  public_id: string;
  station_type: string;
  station_type_display: string;
  min_count: number;
  recommended_count: number;
}

export interface PlanStationRequirementWrite {
  station_type: string;
  min_count: number;
  recommended_count: number;
}

export interface PlanLabelTemplate {
  public_id: string;
  name: string;
  label_type: string;
  label_type_display: string;
  size: string;
  size_display: string;
  background_color: string;
  text_color: string;
  content_template: string;
  color_scheme: string;
  quantity_needed: number;
  placement_guidance: string;
  template_html: string;
  fields_json: LabelTemplateFields;
  data: MetadataBlob;
  created_at: string;
  updated_at: string;
}

export interface PlanLabelTemplateWrite {
  name: string;
  label_type: string;
  size?: string;
  content_template?: string;
  color_scheme?: string;
  quantity_needed?: number;
  placement_guidance?: string;
}

// =============================================================================
// Domain 2: Reunification Sites
// =============================================================================

/** Reunification site — backed by schools.School with data JSONField. */
export interface ReunificationSite {
  public_id: string;
  name: string;
  site_type: SiteType | string;
  district: number | null;
  district_name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  latitude: string | null;
  longitude: string | null;
  total_capacity: number;
  parking_capacity: number;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  status: string;
  floor_count: number;
  site_type_display?: string;
}

export interface ReunificationSiteDetail extends ReunificationSite {
  indoor_capacity: number;
  outdoor_capacity: number;
  access_instructions: string;
  accessibility_notes: string;
  is_verified: boolean;
  /** Rooms in this site's buildings (replaces SiteFeature[]) */
  rooms: Array<{ public_id: string; name: string; room_type?: string }>;
  /** Floors in this site's buildings (replaces SiteFloorplan[]) */
  floors: Array<{ public_id: string; name: string; floor_number?: number }>;
  geofences: SiteGeoFence[];
  updated_at: string;
}

export interface SiteFloorplan {
  public_id: string;
  name: string;
  floorplan_type: string;
  floorplan_type_display: string;
  floor_number: number;
  floor_label: string;
  image_url: string;
  sort_order: number;
  features: SiteFeature[];
  geofences: SiteGeoFence[];
  created_at: string;
  updated_at: string;
}

export interface SiteFeature {
  public_id: string;
  name: string;
  feature_type: string;
  feature_type_display: string;
  description: string;
  latitude: number | null;
  longitude: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SiteGeoFence {
  public_id: string;
  name: string;
  fence_type: string;
  fence_type_display: string;
  boundary_type: string;
  boundary_type_display: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// Domain 3: Runtime / Events
// =============================================================================

export interface ReunificationEvent {
  public_id: string;
  alert: number;
  school_name: string;
  alert_type: string;
  plan_template: string | null;
  plan_name: string | null;
  incident_type: IncidentType;
  incident_type_display: string;
  status: ReunificationStatus;
  status_display: string;
  is_drill: boolean;
  primary_site_name: string | null;
  started_at: string | null;
  completed_at: string | null;
  stats: EventStats;
  created_at: string;
}

export interface ReunificationEventDetail extends ReunificationEvent {
  require_leo_interview: boolean;
  require_counseling: boolean;
  paused_at: string | null;
  sites: EventSite[];
  staff_assignments: EventStaffAssignment[];
  label_assignments: EventLabelAssignment[];
  notification_settings: EventNotificationSettings;
  statistics: EventMetrics;
  created_by: number | null;
  created_by_name: string | null;
  data: MetadataBlob;
  updated_at: string;
}

export interface EventStats {
  total_students: number;
  students_reunified: number;
  students_in_assembly: number;
  students_missing: number;
  students_medical: number;
  total_guardians: number;
  guardians_checked_in: number;
}

export function accountedFromStats(s: EventStats): number {
  return (
    s.students_reunified +
    s.students_in_assembly +
    s.students_missing +
    s.students_medical
  );
}

export function unaccountedFromStats(s: EventStats): number {
  return Math.max(0, s.total_students - accountedFromStats(s));
}

export interface EventSite {
  public_id: string;
  site: string;
  site_name: string;
  site_type: string;
  role: string;
  role_display: string;
  is_active: boolean;
  activated_at: string | null;
  notes: string;
}

export interface EventStaffAssignment {
  public_id: string;
  staff_member: number;
  staff_name: string;
  station_assignment: string | null;
  station_name: string | null;
  role: StaffRole;
  role_display: string;
  status: StaffStatus;
  status_display: string;
  assigned_at: string;
  checked_in_at: string | null;
  released_at: string | null;
  notes: string;
}

/**
 * Write shape for POST `/events/{event_public_id}/staff/` and
 * PATCH `/events/{event_public_id}/staff/{public_id}/`. Mirrors
 * `EventStaffAssignmentRequest` from the generated OpenAPI schema —
 * `staff_member` is the auth-user PK, `station_assignment` is the
 * EventStationAssignment PK (nullable to clear an existing assignment).
 * Used by `createEventStaff` / `updateEventStaff` per ADR-113.
 */
export interface EventStaffAssignmentWrite {
  staff_member: number;
  role: StaffRole;
  station_assignment?: number | null;
  notes?: string;
}

export interface EventLabelAssignment {
  public_id: string;
  label_template: string;
  label_name: string;
  label_type: string;
  room: string | null;
  room_name: string | null;
  is_placed: boolean;
  placed_at: string | null;
  placed_by: number | null;
  placed_by_name: string | null;
  photo_url: string;
  notes: string;
}

export interface StudentReunification {
  public_id: string;
  roster_person: number;
  student_name: string;
  grade: string;
  homeroom: string;
  status: StudentStatus;
  status_display: string;
  zone_name: string | null;
  requires_leo_interview: boolean;
  leo_interview_completed: boolean;
  requires_counseling: boolean;
  counseling_completed: boolean;
  is_cleared: boolean;
  accounted_at: string | null;
  released_at: string | null;
  created_at: string;
  // Editable detail fields. Populated only on the detail endpoint
  // (`useEventStudent`) — list responses leave them undefined.
  // `medical_notes` is FERPA-gated: absent from the response when the
  // requesting user lacks `reunification.view_student_medical_notes`,
  // regardless of value on the underlying record.
  notes?: string;
  medical_notes?: string;
  special_instructions?: string;
  supervising_staff?: number | null;
  supervising_staff_name?: string | null;
  station_name?: string | null;
  current_zone?: number | null;
  current_station?: number | null;
}

/**
 * Write shape for POST `/events/{event_public_id}/students/` and
 * PATCH `/events/{event_public_id}/students/{public_id}/`. Mirrors
 * the writable fields on `StudentReunificationSerializer` —
 * `roster_person` is the integer RosterPerson PK (exposed as `id` on
 * `PlanningStudent`). FERPA: `medical_notes` is gated on the server —
 * writing it without `reunification.edit_student_medical_notes` returns 403.
 */
export interface StudentReunificationWrite {
  roster_person?: number;
  notes?: string;
  medical_notes?: string;
  special_instructions?: string;
  requires_leo_interview?: boolean;
  requires_counseling?: boolean;
  supervising_staff?: number | null;
}

export interface GuardianReunification {
  public_id: string;
  roster_person: number;
  guardian_name: string;
  status: GuardianStatus;
  status_display: string;
  is_pre_verified: boolean;
  student_count: number;
  station_name: string | null;
  arrived_at: string | null;
  verified_at: string | null;
  /** Set by the basic serializer on retrieve / create / patch. List view omits. */
  verification_method?: string | null;
  /** Set by the basic serializer on retrieve / create / patch. List view omits. */
  id_type?: string | null;
  /** UUIDs of StudentReunification rows this guardian is authorized for. */
  authorized_students?: string[];
  /** Pre-computed display labels from the basic serializer. */
  authorized_student_names?: string[];
  phone?: string;
  email?: string;
}

/**
 * Write shape for POST `/events/{event_public_id}/guardians/` and
 * PATCH `/events/{event_public_id}/guardians/{public_id}/`.
 * Mirrors `GuardianReunificationRequest` from the generated OpenAPI schema —
 * `roster_person` is the numeric RosterPerson PK,
 * `authorized_students` is a list of StudentReunification UUIDs (M2M).
 *
 * ## Compliance gate
 *
 * Restricted-PII tier (CCPA/CPRA in scope). Edits to `authorized_students`
 * are sensitive — the backend writes an EventAssignmentLog row with old
 * + new value so the privacy audit-log captures the change (ADR-099 §Layer 5).
 */
export interface GuardianReunificationWrite {
  roster_person: number;
  is_pre_verified?: boolean;
  verification_method?: string | null;
  id_type?: string | null;
  authorized_students?: string[];
}

// =============================================================================
// API Response Types
// =============================================================================

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export type PlanTemplateListResponse = PaginatedResponse<PlanTemplate>;
export type ReunificationSiteListResponse = PaginatedResponse<ReunificationSite>;
export type ReunificationEventListResponse = PaginatedResponse<ReunificationEvent>;
export type StudentListResponse = PaginatedResponse<StudentReunification>;
export type GuardianListResponse = PaginatedResponse<GuardianReunification>;
export type StaffAssignmentListResponse = PaginatedResponse<EventStaffAssignment>;

// =============================================================================
// Pre-Event Planning: Roster Students
// =============================================================================

export interface PlanningStudent {
  /**
   * Numeric RosterPerson PK — the SMS student picker POSTs it as the
   * `roster_person` FK on POST `/events/{event_public_id}/students/`, and the
   * SMS guardian picker does the same on
   * POST `/events/{event_public_id}/guardians/`. Mirrors the staff picker,
   * which uses `staff_member: <auth_user_pk>`. UUID `public_id` remains the
   * canonical external identifier.
   */
  id: number;
  public_id: string;
  first_name: string;
  last_name: string;
  external_reference_id: string;
  /**
   * SIS-sourced RosterPerson role (`student` / `staff` / `administrator` /
   * `aide` / `guardian`). Exposed so the same endpoint serves both student-
   * and guardian-scoped pickers via the `?role=` filter.
   */
  role: string;
  school: number | null;
  school_name: string | null;
  grade: string | null;
  created_at: string;
  updated_at: string;
}

export type PlanningStudentListResponse = PaginatedResponse<PlanningStudent>;

export interface PlanningStudentFilters {
  search?: string;
  /**
   * Filter by RosterPerson role — defaults to `student`; the endpoint also
   * serves `guardian` and `staff` rows. Pass `'guardian'` for the guardian
   * picker.
   */
  role?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
  school?: number;
}

// =============================================================================
// Filter Types
// =============================================================================

export interface PlanTemplateFilters {
  search?: string;
  is_published?: boolean;
  incident_type?: IncidentType;
  /**
   * When true, restrict results to system templates (district=null,
   * is_system_template=True). Used by AddPlanDialog to fetch the canonical
   * ILUG Standard Reunification Method template seeded by the
   * `seed_ilug_reunify` management command.
   */
  is_system_template?: boolean;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface SiteFilters {
  search?: string;
  site_type?: SiteType;
  is_active?: boolean;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface ReunificationEventFilters {
  search?: string;
  status?: ReunificationStatus;
  is_drill?: boolean;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface StudentFilters {
  search?: string;
  status?: StudentStatus;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface GuardianFilters {
  search?: string;
  status?: GuardianStatus;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface StaffFilters {
  search?: string;
  role?: StaffRole;
  status?: StaffStatus;
  page?: number;
  page_size?: number;
  ordering?: string;
}

// =============================================================================
// Domain 4: Staff Role Pre-Assignments
// =============================================================================

export interface PlanStaffAssignment {
  public_id: string;
  user: number;
  user_name: string;
  role: StaffRole;
  role_display: string;
  plan_template: string | null;
  plan_template_name: string | null;
}

export interface PlanStaffAssignmentWrite {
  user: number;
  role: StaffRole;
  plan_template?: string | null;
}

export interface PlanStaffAssignmentFilters {
  user?: number;
  role?: StaffRole;
  plan_template?: string;
  plan_template__isnull?: boolean;
  search?: string;
  ordering?: string;
  page_size?: number;
}

export interface PlanStaffAssignmentListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PlanStaffAssignment[];
}

// =============================================================================
// Form Initial Values
// =============================================================================

export type NotificationChannel = 'sms' | 'email' | 'push';

export const NOTIFICATION_CHANNEL_OPTIONS = [
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'push', label: 'Push Notification' },
];

export interface PlanTemplateFormValues {
  name: string;
  description: string;
  incident_type: IncidentType | '';
  reunification_mode: ReunificationMode | '';
  scale: PlanScale | '';
  default_leo_interview: boolean;
  default_counseling: boolean;
  special_procedures: string;
  estimated_duration_hours: number | '';
  is_published: boolean;
  guardian_verification_required: boolean;
  require_id_verification: boolean;
  require_photo_capture: boolean;
  require_signature: boolean;
  notify_on_activation: boolean;
  notification_channels: string[];
}

export const PlanTemplateInitialValues: PlanTemplateFormValues = {
  name: '',
  description: '',
  incident_type: '',
  reunification_mode: '',
  scale: '',
  default_leo_interview: false,
  default_counseling: false,
  special_procedures: '',
  estimated_duration_hours: '',
  is_published: false,
  guardian_verification_required: true,
  require_id_verification: false,
  require_photo_capture: false,
  require_signature: false,
  notify_on_activation: true,
  notification_channels: ['sms'],
};

// =============================================================================
// Display Option Constants
// =============================================================================

export const INCIDENT_TYPE_OPTIONS = [
  { value: 'fire', label: 'Fire' },
  { value: 'lockdown', label: 'Lockdown' },
  { value: 'active_threat', label: 'Active Threat' },
  { value: 'weather_shelter', label: 'Weather - Shelter in Place' },
  { value: 'weather_evac', label: 'Weather - Evacuation' },
  { value: 'hazmat', label: 'Hazmat' },
  { value: 'medical', label: 'Medical Emergency' },
  { value: 'bomb_threat', label: 'Bomb Threat' },
  { value: 'gas_leak', label: 'Gas Leak' },
  { value: 'other', label: 'Other' },
];

export const REUNIFICATION_MODE_OPTIONS = [
  { value: 'onsite', label: 'Onsite' },
  { value: 'offsite', label: 'Offsite' },
  { value: 'either', label: 'Either' },
];

export const PLAN_SCALE_OPTIONS = [
  { value: 'single_location', label: 'Single Location' },
  { value: 'multi_location', label: 'Multi-Location' },
  { value: 'district_aggregate', label: 'District Aggregate' },
];

export const SITE_TYPE_OPTIONS = [
  { value: 'school', label: 'School' },
  { value: 'mou_location', label: 'MOU Location' },
  { value: 'community_center', label: 'Community Center' },
  { value: 'church', label: 'Church/Place of Worship' },
  { value: 'stadium', label: 'Stadium/Arena' },
  { value: 'park', label: 'Park/Open Space' },
  { value: 'parking_lot', label: 'Parking Lot' },
  { value: 'convention_center', label: 'Convention Center' },
  { value: 'ad_hoc', label: 'Ad-Hoc Location' },
  { value: 'other', label: 'Other' },
];

export const STAFF_ROLE_OPTIONS = [
  { value: 'incident_commander', label: 'Incident Commander' },
  { value: 'greeter', label: 'Greeter' },
  { value: 'checker', label: 'Checker (ID Verification)' },
  { value: 'reunifier', label: 'Reunifier' },
  { value: 'class_leader', label: 'Class Leader' },
  { value: 'accountant', label: 'Accountant (Recorder)' },
  { value: 'leo_liaison', label: 'LEO Liaison' },
  { value: 'crisis_counselor', label: 'Crisis Counselor' },
  { value: 'bus_coordinator', label: 'Bus Coordinator' },
  { value: 'runner', label: 'Runner' },
  { value: 'releaser', label: 'Releaser' },
  { value: 'recorder', label: 'Recorder' },
  { value: 'scribe', label: 'Scribe' },
  { value: 'medical', label: 'Medical Staff' },
  { value: 'counselor', label: 'Counselor' },
  { value: 'coordinator', label: 'Coordinator' },
  { value: 'other', label: 'Other' },
];

export const STATION_TYPE_OPTIONS = [
  { value: 'greeter', label: 'Greeter' },
  { value: 'check_in', label: 'Check-In' },
  { value: 'waiting_standard', label: 'Waiting Area (Standard)' },
  { value: 'waiting_predator', label: 'Waiting Area (Predator/Custody)' },
  { value: 'waiting_hospital', label: 'Waiting Area (Hospital)' },
  { value: 'assembly', label: 'Assembly Area' },
  { value: 'leo_interview', label: 'LEO Interview' },
  { value: 'crisis_counseling', label: 'Crisis Counseling' },
  { value: 'reunification_room', label: 'Reunification Room' },
  { value: 'exit', label: 'Exit Checkpoint' },
  { value: 'medical', label: 'Medical/First Aid' },
  { value: 'command_post', label: 'Command Post' },
  { value: 'media_staging', label: 'Media Staging' },
  { value: 'bus_staging', label: 'Bus Staging' },
  { value: 'other', label: 'Other' },
];

export const LABEL_TYPE_OPTIONS = [
  { value: 'station_sign', label: 'Station Sign' },
  { value: 'directional', label: 'Directional' },
  { value: 'zone_marker', label: 'Zone Marker' },
  { value: 'qr_code', label: 'QR Code' },
  { value: 'banner', label: 'Banner' },
  { value: 'floor_marking', label: 'Floor Marking' },
  { value: 'a_frame', label: 'A-Frame' },
  { value: 'other', label: 'Other' },
];

export const LABEL_SIZE_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'banner', label: 'Banner' },
];

export const STUDENT_STATUS_OPTIONS = [
  { value: 'unaccounted', label: 'Unaccounted' },
  { value: 'accounted', label: 'Accounted' },
  { value: 'secured_at_incident', label: 'Secured at Incident' },
  { value: 'transported', label: 'Transported' },
  { value: 'arrived_at_site', label: 'Arrived at Site' },
  { value: 'in_assembly', label: 'In Assembly' },
  { value: 'in_leo_interview', label: 'In LEO Interview' },
  { value: 'in_crisis_counseling', label: 'In Crisis Counseling' },
  { value: 'cleared_for_release', label: 'Cleared for Release' },
  { value: 'in_reunification_room', label: 'In Reunification Room' },
  { value: 'reunified', label: 'Reunified' },
  { value: 'exited', label: 'Exited' },
  { value: 'reported_safe_offsite', label: 'Reported Safe Offsite' },
  { value: 'reunified_offsite', label: 'Reunified Offsite' },
  { value: 'safe_at_home', label: 'Safe at Home' },
  { value: 'at_hospital', label: 'At Hospital' },
  { value: 'released_to_third_party', label: 'Released to Third Party' },
  { value: 'self_transported', label: 'Self Transported' },
  { value: 'absent', label: 'Absent' },
  { value: 'missing', label: 'Missing' },
  { value: 'medical', label: 'Medical' },
  { value: 'with_staff', label: 'With Staff' },
  { value: 'released', label: 'Released' },
];

export const GUARDIAN_STATUS_OPTIONS = [
  { value: 'notified', label: 'Notified' },
  { value: 'pre_verified', label: 'Pre-Verified' },
  { value: 'en_route', label: 'En Route' },
  { value: 'arrived', label: 'Arrived' },
  { value: 'checked_in', label: 'Checked In' },
  { value: 'in_waiting_area', label: 'In Waiting Area' },
  { value: 'matched_to_student', label: 'Matched to Student' },
  { value: 'verified', label: 'Verified' },
  { value: 'reunified', label: 'Reunified' },
  { value: 'exited', label: 'Exited' },
  { value: 'denied', label: 'Denied' },
  { value: 'not_arrived', label: 'Not Arrived' },
  { value: 'released', label: 'Released' },
];

/**
 * Verification method choices for {@link GuardianReunificationWrite.verification_method}.
 * Mirrors the choices on `GuardianVerifySerializer.verification_method`
 * (apps/aegix-aim-backend/server/apps/reunification/serializers.py:1261).
 * The edit form lets operators set this without going through the `verify`
 * action so they can record verifications captured out-of-band (e.g., the
 * guardian was vouched for by a known staff member).
 */
export const VERIFICATION_METHOD_OPTIONS = [
  { value: 'id_scan', label: 'ID Scan' },
  { value: 'face_match', label: 'Face Match' },
  { value: 'manual', label: 'Manual Verification' },
  { value: 'pre_verified', label: 'Pre-Verified via ONE App' },
];

/**
 * ID type choices for {@link GuardianReunificationWrite.id_type}.
 * Mirrors `GuardianVerifySerializer.id_type` (serializers.py:1267).
 */
export const ID_TYPE_OPTIONS = [
  { value: 'drivers_license', label: "Driver's License" },
  { value: 'state_id', label: 'State ID' },
  { value: 'passport', label: 'Passport' },
  { value: 'military_id', label: 'Military ID' },
  { value: 'other', label: 'Other Government ID' },
];

// =============================================================================
// Reporting & Analytics
// =============================================================================

// Field names mirror `EventMetricsSerializer` on the backend
// (apps/aegix-aim-backend/server/apps/reunification/serializers.py). Overview's
// Avg Release Time / Elapsed Time read these names straight off the payload,
// so a name that drifts from the serializer renders undefined / NaN.
export interface EventMetrics {
  total_students: number;
  students_accounted: number;
  students_released: number;
  guardians_arrived: number;
  guardians_verified: number;
  staff_checked_in: number;
  avg_time_to_release_minutes: number | null;
  event_duration_minutes: number | null;
}

export interface TimelineEntry {
  timestamp: string;
  event_type: string;
  description: string;
  entity_type?: string;
  entity_id?: string;
}

export interface DistrictReadinessItem {
  school_name: string;
  plan_count: number;
  ready_plans: number;
  staff_assigned: number;
  last_drill_date: string | null;
  readiness_score: number;
}

/**
 * Sourced from the generated OpenAPI types so the response shape stays in
 * lockstep with ``ReunificationReportSerializer`` on the backend.
 *
 * Section keys (``event_summary``, ``performance_metrics``, etc.) are
 * optional because the backend only emits sections listed in
 * ``include_sections``. PII fields like ``students_summary.records`` are
 * ``null`` unless ``include_pii=true`` is requested AND a FERPA audit
 * log row is written (ADR-099 §Layer 5).
 */
export type ReunificationReport =
  components['schemas']['ReunificationReport'];

/**
 * Report section keys accepted by ``include_sections`` on
 * ``generate_report`` / ``download_report``. Mirrors
 * ``ReportGenerationSerializer.include_sections`` choices on the
 * backend.
 */
export type ReportSection =
  | 'executive_summary'
  | 'event_details'
  | 'student_roster'
  | 'guardian_roster'
  | 'staff_roster'
  | 'timeline'
  | 'metrics'
  | 'zone_summary'
  | 'station_summary'
  | 'release_log'
  | 'compliance'
  | 'recommendations';

// =============================================================================
// Audit Trail
// =============================================================================

export interface EventAssignmentLog {
  public_id: string;
  action: string;
  entity_type: string;
  entity_id: string;
  old_value: string | null;
  new_value: string | null;
  performed_by_name: string;
  timestamp: string;
  notes: string;
}

// The backend `audit_log` action paginates through the viewset's
// ReunificationPagination (page_size 20, max_page_size 100) — see
// reunification/views.py::audit_log. It honours `page` and `page_size`;
// `ordering` is NOT honoured, because the action hardcodes `-timestamp` and
// never calls filter_queryset. Newest-first is therefore always the server order.
export type EventLogListResponse = PaginatedResponse<EventAssignmentLog>;

export interface EventLogFilters {
  page?: number;
  page_size?: number;
}

// =============================================================================
// Configurable Types
// =============================================================================

export interface ConfigurableType {
  public_id: string;
  code: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  sort_order: number;
  is_system: boolean;
  is_active: boolean;
}

export interface ConfigurableTypeWrite {
  code: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  sort_order?: number;
  is_active?: boolean;
}

export interface StaffRoleTypeDetail extends ConfigurableType {
  instructions: string;
  can_release_students: boolean;
  can_verify_guardians: boolean;
  can_override_authorization: boolean;
  permissions: string[];
  required_certifications: string[];
}

export type TypeCategory =
  | 'station-types'
  | 'zone-types'
  | 'staff-role-types'
  | 'label-types'
  | 'note-types';

// =============================================================================
// Reunification Notes (ADR-121 P3)
// =============================================================================

/**
 * Sensitivity tier per ADR-121 §Decision-2 — the enum is lowercase
 * (`'public' | 'sensitive' | 'restricted'`) to match `core/visibility.py`
 * and the generated `VisibilityTierEnum` from the typed http-client. The
 * uppercase wording in the ADR's Decision-2 table is stale spec language
 * scheduled for editorial fix; the implementation is lowercase end-to-end.
 *
 * Server is the source of truth for visibility — the list endpoint omits
 * any note whose tier is not PUBLIC and whose `visibility_permission` the
 * caller lacks. The UI does NOT gate by client-side role check.
 */
export type VisibilityTier = 'public' | 'sensitive' | 'restricted';

/**
 * A NoteType row exposed via
 * `/api/v2/reunification/configurable-types/note-types/`. Adds the two
 * visibility-vocabulary fields (`visibility_tier` +
 * `visibility_permission`) on top of the `ConfigurableType` shape.
 *
 * The `visibility_permission` code is required when `visibility_tier`
 * is not `public` and blank when it is.
 */
export interface NoteType extends ConfigurableType {
  visibility_tier: VisibilityTier;
  /** Django permission code (e.g., `view_medical_note`) — blank when tier is `public`. */
  visibility_permission: string;
}

/**
 * Write shape for `NoteType` (used by the configurable-types admin UI;
 * the typed notes panel itself doesn't write to this surface — it only
 * reads to populate the type selector).
 */
export interface NoteTypeWrite extends ConfigurableTypeWrite {
  visibility_tier?: VisibilityTier;
  visibility_permission?: string;
}

/**
 * A single ReunificationNote row from
 * `/api/v2/reunification/events/{event_public_id}/notes/`.
 *
 * Subject is identified by AT MOST ONE of `student` / `staff` /
 * `guardian` — the backend's CheckConstraint enforces XOR. The
 * `subject_type` / `subject_id` fields are server-computed
 * convenience accessors over the same data.
 *
 * Notes whose type is non-public AND whose `visibility_permission`
 * the caller lacks are simply absent from the list response — there
 * is no flag distinguishing "redacted" from "doesn't exist".
 */
export interface ReunificationNote {
  public_id: string;
  /** UUID of the note's `NoteType` row. */
  note_type: string;
  /** Machine code of the note's type (e.g., `general`, `medical`). */
  note_type_code: string;
  /** Display name of the note's type (e.g., `General`, `Medical`). */
  note_type_name: string;
  /** Visibility tier of the note's type — lowercase enum. */
  visibility_tier: VisibilityTier;
  /** Subject student's public_id, or null if subject is not a student. */
  student: string | null;
  /** Subject staff member's public_id, or null. */
  staff: string | null;
  /** Subject guardian's public_id, or null. */
  guardian: string | null;
  /** Server-computed: `'student' | 'staff' | 'guardian' | null`. */
  subject_type: string | null;
  /** Server-computed subject UUID (matches whichever subject FK is set). */
  subject_id: string | null;
  body: string;
  /** Acting operator's user ID at write time. SET_NULL on user delete. */
  created_by: number | null;
  /** Acting operator's display name (server-formatted). */
  created_by_name: string | null;
  /** ISO 8601 timestamp. */
  created_at: string;
  /** ISO 8601 timestamp. */
  updated_at: string;
}

/**
 * Create payload for `POST /events/{event_public_id}/notes/`.
 *
 * Exactly one of `student` / `staff` / `guardian` MUST be set per
 * backend XOR constraint. `note_type` is the UUID of the chosen
 * `NoteType` row.
 */
export interface ReunificationNoteWrite {
  note_type: string;
  body: string;
  student?: string | null;
  staff?: string | null;
  guardian?: string | null;
}

/**
 * Filter shape for `GET /events/{event_public_id}/notes/`. All
 * filters are optional; omitting them returns all notes the caller is
 * permitted to read (subject to the visibility filter described above).
 */
export interface ReunificationNoteFilters {
  /** Filter to notes whose subject is this student public_id. */
  student?: string;
  /** Filter to notes whose subject is this staff public_id. */
  staff?: string;
  /** Filter to notes whose subject is this guardian public_id. */
  guardian?: string;
  /** Filter to a specific NoteType public_id. */
  note_type?: string;
  /** drf ordering: `-created_at` (default) / `created_at` / `note_type`. */
  ordering?: string;
}

export const REUNIFICATION_TYPE_OPTIONS: { value: TypeCategory; label: string }[] = [
  { value: 'station-types', label: 'Stations' },
  { value: 'zone-types', label: 'Zones' },
  { value: 'staff-role-types', label: 'Staff Roles' },
  { value: 'label-types', label: 'Signage' },
];

export interface ConfigurableTypeFormValues {
  name: string;
  code: string;
  description: string;
  icon: string;
  color: string;
  sort_order: number | '';
  is_active: boolean;
}

export const ConfigurableTypeInitialValues: ConfigurableTypeFormValues = {
  name: '',
  code: '',
  description: '',
  icon: '',
  color: '',
  sort_order: '',
  is_active: true,
};
